import { z } from 'zod';
import { versionSummarySchema, versionedKindSchema } from './versioning.js';

/**
 * The wire contract of `/api/sync/*` (vitrine.md §4.3, §4.4, §8), VERSION 1.
 *
 * The atelier (the dev) talks to the vitrine (the production) and never the
 * other way round (§8.1): every route below is SERVED by the vitrine, behind
 * the machine key `SYNC_KEY`, and CALLED by the atelier through
 * `backend/src/sync/client.ts`. The atelier does not serve them.
 *
 * Posed by the contract lot (#192, lot 0) so that the lots that write the two
 * sides - D for the passage to production, E for the synchronisation - work in
 * parallel against one shape. A lot that has to change a shape here changes it
 * in its own commit and says so in its merge request: the two sides are read
 * by the same schemas, so a drift is a type error and not a surprise on the
 * wire.
 *
 * ## What is typed and what is left `unknown`, on purpose
 *
 * The CASCADE of a passage carries version contents, and a content belongs to
 * the object it is the content of: its schema is the object's own, and the
 * vitrine reads it STRICTLY with its own code (§4.3 - that reading is the whole
 * point of the first lock). So a content travels here as an opaque JSON object,
 * and is never validated by these schemas: validating it with the atelier
 * schema would be the very double reading, by the wrong code, that the lock
 * exists to avoid.
 *
 * The bodies of a session and of its dependencies, of a review and of a room
 * (§8.2, §8.3) were opaque JSON objects in the contract lot; lot E (#192)
 * types them below (`syncSessionBodySchema`, `syncReviewBodySchema`,
 * `syncRoomBodySchema`) and the push entries (`reviewPushSchema`,
 * `trashPushSchema`). The ROWS inside stay opaque JSON objects, on purpose:
 * they are the rows of the tables of the vitrine, and they are read column by
 * column against the tables of the receiver (`backend/src/sync/rows.ts`), not
 * against a second description of the same tables written here. Everything
 * else - the envelopes, the verdict, the refusal codes, the cursor - is typed
 * here and final for version 1.
 */

/** The version of this contract. A change of shape that breaks a reader bumps it. */
export const SYNC_CONTRACT_VERSION = 1;

/**
 * The routes of the vitrine the atelier calls. Paths, relative to the origin of
 * the vitrine (`PROD_ORIGIN`). `/api/instance` is public; every `/api/sync/*`
 * route carries `Authorization: Bearer <SYNC_KEY>`.
 */
export const SYNC_ROUTES = {
  /** GET, public (§2): the role, the code version and the origin of the server. */
  instance: '/api/instance',
  /** POST a `PromotionRequest`, answers a `PromotionVerdict`, changes nothing (§4.3). */
  promotionCheck: '/api/sync/promotion/check',
  /** POST a `PromotionRequest`, answers a `PromotionVerdict`, applies the cascade in one transaction (§4.4). */
  promotionApply: '/api/sync/promotion/apply',
  /** PUT the bytes of ONE media file, see `assetUploadHeadersSchema`. `{id}` is the asset id (§4.4). */
  asset: '/api/sync/assets/{id}',
  /** GET the changes after a cursor, answers a `ChangesPage` (§8.1, §8.2). */
  changes: '/api/sync/changes',
  /** POST a `ReviewPush`, answers a `PushResult` (§8.3). */
  reviews: '/api/sync/reviews',
  /** POST a `TrashPush`, answers a `PushResult` (§8.4). */
  trash: '/api/sync/trash',
} as const;

/* ------------------------------------------------------------- the refusals */

/**
 * The names of a refusal a verdict line can carry (§4.3, §12). A closed
 * catalogue, because the box of the cascade words each of them differently and
 * must not string-match a French sentence.
 *
 * - `unknown-field`: the vitrine code does not know, or reads differently, a
 *   field of the version (§4.3, « lecture stricte »). The line names the field
 *   and where it sits (`path`).
 * - `production-refused`: the refusals the code of the vitrine already opposes
 *   to a passage (the `refuseProduction` hook of the protocol and of the
 *   experience, `production-unresolved`).
 * - `unresolved-pin`: a pinned version that is neither on the vitrine nor in the
 *   cascade, or a `latest` that would have no « latest prod » once the cascade
 *   is applied.
 * - `missing-asset`: a media file the version cites that is neither on the
 *   vitrine nor in the cascade.
 * - `asset-checksum`: the `sha256` of the bytes received does not match the one
 *   declared (§12, « un fichier altéré »).
 * - `prod-unreachable`: the vitrine did not answer. Raised by the ATELIER, never
 *   by the vitrine, so that a box can show the same refusal shape for it.
 * - `version-conflict` (lot D): the vitrine already holds a version of the same
 *   identifier and ANOTHER content, or one whose number is taken by another
 *   version of the entity - the anomaly of section 8.2, met on the way up.
 * - `name-taken` (lot D): the name a gesture gives an entity is held by a living
 *   entity of the vitrine (the birth of an entity, a rename, a restoration).
 * - `unknown-entity` (lot D): a retirement, a restoration, a rename or a pointer
 *   move on an entity the vitrine has never heard of.
 * - `entity-retired` (lot D): a passage or a pointer move on an entity the vitrine
 *   holds retired - it has to be restored first.
 */
export const SYNC_REFUSAL_CODES = [
  'unknown-field',
  'production-refused',
  'unresolved-pin',
  'missing-asset',
  'asset-checksum',
  'prod-unreachable',
  'version-conflict',
  'name-taken',
  'unknown-entity',
  'entity-retired',
] as const;
export const syncRefusalCodeSchema = z.enum(SYNC_REFUSAL_CODES);
export type SyncRefusalCode = z.infer<typeof syncRefusalCodeSchema>;

/** One named refusal: the code, the sentence for the operator, and where in the content it sits. */
export const syncRefusalReasonSchema = z
  .object({
    code: syncRefusalCodeSchema,
    message: z.string().min(1),
    /** A path into the content, e.g. `pages.2.protocol.pin` - absent when the refusal is of the whole version. */
    path: z.string().min(1).optional(),
  })
  .strict();
export type SyncRefusalReason = z.infer<typeof syncRefusalReasonSchema>;

/* ------------------------------------------------------------ the passage */

/** The most versions one cascade carries, and the most files: bounds of input, not of meaning. */
export const PROMOTION_CASCADE_MAX = 500;
export const PROMOTION_ASSETS_MAX = 500;

/** A JSON object whose shape belongs to somebody else - an object content, or the body lot E types. */
export const syncJsonObjectSchema = z.record(z.string(), z.unknown());
export type SyncJsonObject = z.infer<typeof syncJsonObjectSchema>;

/**
 * The gesture that triggers a passage (§4.1): one of the four that touch the
 * vitrine. The discriminant is `type` and not `kind`, because `kind` is already
 * the kind of OBJECT (criterion, persona...) on every variant.
 *
 * - `promote`: « Passer en production » - the version `versionNumber` of the
 *   entity, with its cascade; its `production` pointer advances.
 * - `pointer`: the move of the `production` pointer back to an older version
 *   (the « retour arrière »); the version targeted is already on the vitrine or
 *   travels in the cascade.
 * - `retire` / `restore`: the retirement and the restoration of an entity that
 *   has a version on the vitrine.
 * - `rename`: the new name of such an entity.
 */
export const promotionGestureSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('promote'),
      kind: versionedKindSchema,
      entityId: z.string().min(1),
      versionNumber: z.number().int().positive(),
    })
    .strict(),
  z
    .object({
      type: z.literal('pointer'),
      kind: versionedKindSchema,
      entityId: z.string().min(1),
      versionNumber: z.number().int().positive(),
    })
    .strict(),
  z
    .object({
      type: z.literal('retire'),
      kind: versionedKindSchema,
      entityId: z.string().min(1),
    })
    .strict(),
  z
    .object({
      type: z.literal('restore'),
      kind: versionedKindSchema,
      entityId: z.string().min(1),
    })
    .strict(),
  z
    .object({
      type: z.literal('rename'),
      kind: versionedKindSchema,
      entityId: z.string().min(1),
      name: z.string().trim().min(1).max(120),
    })
    .strict(),
]);
export type PromotionGesture = z.infer<typeof promotionGestureSchema>;

/**
 * One version of the cascade (§4.2): the totality of the version as the atelier
 * stored it, with its identity.
 *
 * `version.id` is the identifier the version keeps on BOTH servers, and
 * `version.number` is its number for life (§4.5): the vitrine writes the row
 * as received, it never numbers. `content` is opaque here, see the header.
 * `advancePointer` is false when the reference that brought the version is
 * pinned on a version older than the « latest prod » of the entity: the version
 * is sent, the pointer is not moved back (§4.2, H18).
 */
export const cascadeVersionSchema = z
  .object({
    kind: versionedKindSchema,
    entityId: z.string().min(1),
    /** The name of the entity at the instant of the passage, for a vitrine that does not know it yet. */
    entityName: z.string().min(1),
    version: z
      .object({
        id: z.string().min(1),
        number: z.number().int().positive(),
        content: syncJsonObjectSchema,
        createdBy: z.string().min(1),
        createdAt: z.string().datetime(),
        note: z.string().nullable(),
      })
      .strict(),
    advancePointer: z.boolean(),
  })
  .strict();
export type CascadeVersion = z.infer<typeof cascadeVersionSchema>;

/**
 * The identifier of a media file on the wire (lot D): the library's own ids are opaque hex, and
 * this one becomes a PATH on the vitrine - a temporary name, then the name of the file - so it is
 * constrained to the characters of a file name and nothing else. A `..` or a separator never
 * crosses.
 */
export const syncAssetIdSchema = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);

/**
 * A media file a version of the cascade cites (§4.2). Only its description
 * travels in the request: the BYTES go apart, one `PUT` per file on
 * `SYNC_ROUTES.asset` (see `assetUploadHeadersSchema`), written before the
 * transaction under a temporary name and renamed at its success (§4.4).
 */
export const syncAssetSchema = z
  .object({
    id: syncAssetIdSchema,
    /** Lowercase hexadecimal SHA-256 of the bytes, verified on arrival (§4.3). */
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    mime: z.string().min(1),
    byteLength: z.number().int().nonnegative(),
    name: z.string().min(1),
    /**
     * What the row of the library says besides the bytes (lot D): the vitrine
     * rebuilds the format, the kind and the dimensions from the name and the
     * bytes, but the licence and the tags are the team's words and travel.
     */
    license: z.string().default(''),
    tags: z.array(z.string()).default([]),
  })
  .strict();
export type SyncAsset = z.infer<typeof syncAssetSchema>;
/** What a `SyncAsset` is when it is built, before the defaults. */
export type SyncAssetInput = z.input<typeof syncAssetSchema>;

/**
 * The headers of the upload of ONE file, `PUT /api/sync/assets/{id}` with the
 * raw bytes as the body. Header names are lowercase, as a server reads them.
 * The vitrine hashes what it received and compares it to `x-asset-sha256`
 * (`asset-checksum` on a mismatch), and to the `byteLength` of the request.
 */
export const assetUploadHeadersSchema = z
  .object({
    'content-type': z.string().min(1),
    'content-length': z.string().regex(/^\d+$/),
    'x-asset-sha256': z.string().regex(/^[0-9a-f]{64}$/),
  })
  .passthrough();
export type AssetUploadHeaders = z.infer<typeof assetUploadHeadersSchema>;

/**
 * The body of `POST /api/sync/promotion/check` and `.../apply` (§4.3, §4.4):
 * the same request, judged first and applied then. The vitrine replays its
 * judgment at the apply, the content having possibly changed in between.
 */
export const promotionRequestSchema = z
  .object({
    gesture: promotionGestureSchema,
    /** The versions to send, in dependency order (leaves first). Empty for a retirement or a rename. */
    cascade: z.array(cascadeVersionSchema).max(PROMOTION_CASCADE_MAX),
    /** The media files the cascade cites. */
    assets: z.array(syncAssetSchema).max(PROMOTION_ASSETS_MAX),
  })
  .strict();
export type PromotionRequest = z.infer<typeof promotionRequestSchema>;

/**
 * What the vitrine says of one version of the cascade (§4.3):
 * `sends` - it will be written; `already` - it is on the vitrine and not sent
 * again; `refused` - it cannot be read or played, `reason` says why.
 */
export const promotionLineStatusSchema = z.enum(['sends', 'already', 'refused']);
export type PromotionLineStatus = z.infer<typeof promotionLineStatusSchema>;

export const promotionVerdictLineSchema = z
  .object({
    kind: versionedKindSchema,
    entityId: z.string().min(1),
    versionNumber: z.number().int().positive(),
    status: promotionLineStatusSchema,
    /** Present when, and only when, `status` is `refused`. */
    reason: syncRefusalReasonSchema.optional(),
    /**
     * Whether the `production` pointer of the vitrine sits on THIS version once the cascade is in (lot
     * D). The atelier mirrors exactly that: the vitrine may decline to move a pointer (it never goes
     * back through a cascade, H18), and a pointer the atelier moved from its own plan would then say
     * what the prod does not play.
     */
    pointerPlaced: z.boolean().optional(),
  })
  .strict();
export type PromotionVerdictLine = z.infer<typeof promotionVerdictLineSchema>;

/** What the vitrine says of one media file of the request (lot D), at the shape of a line. */
export const promotionAssetLineSchema = z
  .object({
    id: syncAssetIdSchema,
    status: promotionLineStatusSchema,
    /** Present when, and only when, `status` is `refused`. */
    reason: syncRefusalReasonSchema.optional(),
  })
  .strict();
export type PromotionAssetLine = z.infer<typeof promotionAssetLineSchema>;

/**
 * The answer of `PUT /api/sync/assets/{id}` (lot D): the file is held, under a temporary
 * name, until the apply that cites it - which gives it its name - or forgotten.
 */
export const assetReceivedSchema = z
  .object({
    id: syncAssetIdSchema,
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    byteLength: z.number().int().nonnegative(),
  })
  .strict();
export type AssetReceived = z.infer<typeof assetReceivedSchema>;

/**
 * The answer to `check` and to `apply` (§4.3, §4.4). A JUDGMENT IS NOT AN
 * ERROR: a cascade with a refused line comes back as a 200 with
 * `applied: false` and the line named, and nothing was written. A 4xx is for a
 * request the vitrine could not even judge (see `syncRefusalSchema`).
 *
 * `prodCode` is the version of the code that judged (the `code` of
 * `GET /api/instance`), so the refusal can say « La prod (v1.3.0) ne connaît
 * pas... ». `applied` is true only on an `apply` that wrote everything.
 */
export const promotionVerdictSchema = z
  .object({
    prodCode: z.string().min(1),
    applied: z.boolean(),
    lines: z.array(promotionVerdictLineSchema),
    /**
     * What the vitrine says of each media file of the request (lot D): `sends` - the
     * bytes are not there, the atelier sends them (`PUT` on `SYNC_ROUTES.asset`)
     * before the apply; `already` - the file is on the vitrine; `refused` - the
     * request cites it wrongly. Empty for a vitrine that predates the lot.
     */
    assets: z.array(promotionAssetLineSchema).default([]),
    /**
     * The refusal of the GESTURE itself, when it is not about a line (lot D): a
     * retirement of an entity the vitrine does not know, a rename to a taken name.
     * Absent when every refusal sits on a line.
     */
    gestureRefusal: syncRefusalReasonSchema.optional(),
  })
  .strict();
export type PromotionVerdict = z.infer<typeof promotionVerdictSchema>;

/**
 * The body of a 4xx of the vitrine on a `/api/sync/*` route: the envelope the
 * error handler of the repo already writes (`{ error, message, details }`),
 * with the verdict riding along when a refusal has one. `error` is the code -
 * `UNAUTHORIZED`, `sync-disabled`, `VALIDATION_ERROR`... - and `message` is for
 * a log.
 */
export const syncRefusalSchema = z
  .object({
    error: z.string().min(1),
    message: z.string().min(1),
    details: z.unknown().optional(),
    verdict: promotionVerdictSchema.optional(),
  })
  .passthrough();
export type SyncRefusal = z.infer<typeof syncRefusalSchema>;

/* ---------------------------------------------------------- the changes flow */

/** The default and the ceiling of a page of the flow (H26: of the order of fifty sessions). */
export const CHANGES_PAGE_DEFAULT_LIMIT = 50;
export const CHANGES_PAGE_MAX_LIMIT = 200;

/**
 * The query of `GET /api/sync/changes` (§8.1): the cursor the atelier last
 * received (0 for the first synchronisation) and the size of the page.
 */
export const changesQuerySchema = z
  .object({
    after: z.coerce.number().int().nonnegative().default(0),
    limit: z.coerce
      .number()
      .int()
      .positive()
      .max(CHANGES_PAGE_MAX_LIMIT)
      .default(CHANGES_PAGE_DEFAULT_LIMIT),
  })
  .strict();
export type ChangesQuery = z.infer<typeof changesQuerySchema>;

/** The `seq` every change carries: the monotone number of `sync_changes` on the vitrine (§8.1). */
const changeSeq = z.number().int().positive();

/**
 * The `production` pointer of one entity on the vitrine, with the versions the
 * vitrine holds - what the atelier realigns its marks and pointers on (§4.4,
 * §8.2). `productionVersionId` is null when the entity has no pointer there.
 */
export const syncPointerSchema = z
  .object({
    kind: versionedKindSchema,
    entityId: z.string().min(1),
    productionVersionId: z.string().min(1).nullable(),
    /** The identifiers of every version of the entity present on the vitrine, i.e. the « en prod » marks. */
    versionIds: z.array(z.string().min(1)),
  })
  .strict();
export type SyncPointer = z.infer<typeof syncPointerSchema>;

/* ------------------------------------------------- the bodies (lot E, #192) */

/**
 * The kinds of versioned object a session freezes (§8.2): the five of
 * `versionedKindSchema`, plus the two singletons of the vitrine, Modeles and
 * Regles (§5), which a session cites by `models_version_id` and
 * `rules_version_id`.
 */
export const SYNC_FROZEN_KINDS = [...versionedKindSchema.options, 'models', 'rules'] as const;
export const syncFrozenKindSchema = z.enum(SYNC_FROZEN_KINDS);
export type SyncFrozenKind = z.infer<typeof syncFrozenKindSchema>;

/**
 * A version a session froze, with its CONTENT (§8.2): what the atelier stores
 * when it does not have it, marked "en prod". The content is opaque here, for
 * the reason the header gives for the cascade of a passage.
 */
export const syncFrozenVersionSchema = z
  .object({
    kind: syncFrozenKindSchema,
    entityId: z.string().min(1),
    entityName: z.string().min(1),
    version: z
      .object({
        id: z.string().min(1),
        number: z.number().int().positive(),
        content: syncJsonObjectSchema,
        createdBy: z.string().min(1),
        createdAt: z.string().min(1),
        note: z.string().nullable(),
      })
      .strict(),
  })
  .strict();
export type SyncFrozenVersion = z.infer<typeof syncFrozenVersionSchema>;

/**
 * The body of a `session` change: the row of the session WITHOUT its token (the
 * credential of the participant never leaves the vitrine), the row of its link
 * WITHOUT its secret, the rows that depend on it, and the versions it froze.
 *
 * `versions` holds a version once PER PAGE of the flow, not once per session
 * (a hundred sessions of one experience would otherwise carry its content a
 * hundred times): the receiver collects the versions of the whole page before it
 * applies a session of it.
 */
export const syncSessionBodySchema = z
  .object({
    session: syncJsonObjectSchema,
    link: syncJsonObjectSchema.nullable(),
    turns: z.array(syncJsonObjectSchema),
    turnPrompts: z.array(syncJsonObjectSchema),
    pageVisits: z.array(syncJsonObjectSchema),
    noyauDetermination: syncJsonObjectSchema.nullable(),
    noyauPrompts: syncJsonObjectSchema.nullable(),
    noyauChoices: z.array(syncJsonObjectSchema),
    rangement: syncJsonObjectSchema.nullable(),
    rangementPrompts: syncJsonObjectSchema.nullable(),
    /**
     * The bubbles of the pages played in the conversation and the input each was
     * served (prolongation.md §7, issue #186): two tables of the session, replaced
     * whole like the others. DEFAULTED, because a vitrine running the code of
     * before the issue sends neither - which reads as a session with no page of
     * the thread, and is one.
     */
    conversationBubbles: z.array(syncJsonObjectSchema).default([]),
    conversationBubblePrompts: z.array(syncJsonObjectSchema).default([]),
    versions: z.array(syncFrozenVersionSchema),
  })
  .strip();
export type SyncSessionBody = z.infer<typeof syncSessionBodySchema>;

/**
 * One line of the journal of a review as it travels (§8.3): the very row of
 * `session_review_events`, so that an element has the same identifier, author
 * and instant on both servers. `before` and `after` are the element before and
 * after the gesture, as the journal stores them.
 */
export const syncReviewEventSchema = z
  .object({
    id: z.string().min(1),
    // A string and not the enum: the atelier is ahead of the vitrine, and a line of an operation the
    // other side does not know must be KEPT in its journal and merge nothing, not refuse the push.
    op: z.string().min(1),
    before: z.unknown().nullable(),
    after: z.unknown().nullable(),
    by: z.string().min(1),
    at: z.string().min(1),
    via: z.string().nullable(),
    dapres: z.string().nullable(),
  })
  .strip();
export type SyncReviewEvent = z.infer<typeof syncReviewEventSchema>;

/**
 * The body of a `review` change: the review of a session as the vitrine holds it,
 * the whole of it (a review has a few dozen elements at most) - the row that
 * froze the grid, the elements of the six kinds, and the journal.
 */
export const syncReviewBodySchema = z
  .object({
    /** The row of `session_reviews` (the frozen grid, its instant and author), or null before any gesture. */
    frozen: syncJsonObjectSchema.nullable(),
    extracts: z.array(syncJsonObjectSchema),
    caseNotes: z.array(syncJsonObjectSchema),
    validations: z.array(syncJsonObjectSchema),
    questionNotes: z.array(syncJsonObjectSchema),
    noyau: syncJsonObjectSchema.nullable(),
    notes: z.array(syncJsonObjectSchema),
    events: z.array(syncJsonObjectSchema),
  })
  .strip();
export type SyncReviewBody = z.infer<typeof syncReviewBodySchema>;

/**
 * The body of a `room` change (V12): the row of the room WITHOUT its two tokens
 * (the links of the room stay on the vitrine), its two participants and its
 * transcript.
 */
export const syncRoomBodySchema = z
  .object({
    room: syncJsonObjectSchema,
    participants: z.array(syncJsonObjectSchema),
    messages: z.array(syncJsonObjectSchema),
  })
  .strip();
export type SyncRoomBody = z.infer<typeof syncRoomBodySchema>;

/**
 * One change of the flow, discriminated on `kind` (§8.2). Each one carries its
 * `seq`.
 *
 * - `session`: a session of the vitrine and everything that depends on it
 *   (turns, served inputs, page visits, noyau, rangement, feedback, the line of
 *   its link, the versions it froze with their content). A session in progress
 *   comes again, whole, at every change.
 * - `review`: the review of a session and its journal (§8.3).
 * - `trash`: the trash state of a session (§8.4): `deletedAt` is the date it
 *   went to the trash, null when it was restored.
 * - `tombstone`: the trace of an erasure, no content (§8.4).
 * - `room`: an interview room, its participants and transcript (V12).
 * - `pointers`: the `production` pointers of the vitrine (§4.4, §8.2).
 */
export const changeSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('session'),
      seq: changeSeq,
      sessionId: z.string().min(1),
      body: syncSessionBodySchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('review'),
      seq: changeSeq,
      sessionId: z.string().min(1),
      body: syncReviewBodySchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('trash'),
      seq: changeSeq,
      sessionId: z.string().min(1),
      deletedAt: z.string().datetime().nullable(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('tombstone'),
      seq: changeSeq,
      sessionId: z.string().min(1),
      purgedAt: z.string().datetime(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('room'),
      seq: changeSeq,
      roomId: z.string().min(1),
      body: syncRoomBodySchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('pointers'),
      seq: changeSeq,
      pointers: z.array(syncPointerSchema),
    })
    .strict(),
]);
export type Change = z.infer<typeof changeSchema>;

/**
 * The kinds of change, as a list the `sync_changes` table stores (its `kind`
 * column is guarded by this enum at the write). It must stay the list of the
 * discriminants of `changeSchema` above - a test holds the two together.
 */
export const SYNC_CHANGE_KINDS = [
  'session',
  'review',
  'trash',
  'tombstone',
  'room',
  'pointers',
] as const;
export const syncChangeKindSchema = z.enum(SYNC_CHANGE_KINDS);
export type SyncChangeKind = z.infer<typeof syncChangeKindSchema>;

/** The server that purged a session, on a trace of erasure (`sync_tombstones.origin`, section 8.4). */
export const syncTombstoneOriginSchema = z.enum(['prod', 'dev']);
export type SyncTombstoneOrigin = z.infer<typeof syncTombstoneOriginSchema>;

/**
 * A page of the flow (§8.1, H26). `next` is the cursor to ask for the next page
 * (the `seq` of the last change of the page, or the cursor asked when the page
 * is empty); `done` is true when nothing follows - the atelier stops there, and
 * stores `next` only once the page is applied.
 */
export const changesPageSchema = z
  .object({
    changes: z.array(changeSchema),
    next: z.number().int().nonnegative(),
    done: z.boolean(),
  })
  .strict();
export type ChangesPage = z.infer<typeof changesPageSchema>;

/**
 * The page as a READER takes it across two versions of the code (issue #192): the changes are
 * read one by one by the receiver, so that a change of a kind this code does not know - the
 * dev is ahead of the prod by construction - is set aside and counted instead of refusing the
 * whole page. The cursor (next) and the end (done) stay strict: without them there is no cursor.
 */
export const changesPageLooseSchema = z
  .object({
    changes: z.array(z.unknown()),
    next: z.number().int().nonnegative(),
    done: z.boolean(),
  })
  .strip();

/* ---------------------------------------------------- what the atelier sends */

/**
 * The review changes the atelier sends to the vitrine (§8.3): the one write
 * that goes from the atelier to the vitrine outside a passage. Per session, the
 * operations laid down since the last synchronisation: a line of the journal
 * (`syncReviewEventSchema`), which carries the element before and after the
 * gesture - an extract, a case note, a validation, a question annotation, the
 * noyau, a session note - with its identifier, author and instant. The vitrine
 * merges element by element and prevails in case of doubt (backend
 * `sync/review-merge.ts`).
 *
 * `frozen` is the row that froze the grid on the atelier (or null): the vitrine
 * takes it only when it has frozen none, its own grid prevailing otherwise.
 */
export const reviewPushSchema = z
  .object({
    reviews: z.array(
      z
        .object({
          sessionId: z.string().min(1),
          frozen: syncJsonObjectSchema.nullable(),
          operations: z.array(syncReviewEventSchema),
        })
        .strip(),
    ),
  })
  .strict();
export type ReviewPush = z.infer<typeof reviewPushSchema>;

/**
 * The trash changes the atelier sends (§8.4): the sessions put to the trash or
 * restored on the atelier since the last synchronisation (`deletedAt` null =
 * restored), and the tombstones of the sessions it purged. The vitrine prevails
 * in case of doubt, and applies a tombstone even if the trash state never
 * reached it.
 */
export const trashPushSchema = z
  .object({
    /**
     * The cursor the atelier last applied: a trash change of the vitrine after
     * it has not been seen by the atelier, so a session whose trash state moved
     * on BOTH sides since is a doubt, and the vitrine prevails (§8.4).
     */
    cursor: z.number().int().nonnegative(),
    trash: z.array(
      z
        .object({
          sessionId: z.string().min(1),
          deletedAt: z.string().datetime().nullable(),
        })
        .strict(),
    ),
    tombstones: z.array(
      z
        .object({
          sessionId: z.string().min(1),
          purgedAt: z.string().datetime(),
        })
        .strict(),
    ),
  })
  .strict();
export type TrashPush = z.infer<typeof trashPushSchema>;

/**
 * The answer to a push (`reviews`, `trash`): how many units the vitrine took
 * into account. Deliberately small - a lot that needs the conflicts reported
 * extends it, and the atelier reads it with this schema, which strips what it
 * does not know.
 */
export const pushResultSchema = z
  .object({
    applied: z.number().int().nonnegative(),
    /** The units the vitrine set aside because it prevailed (an element changed on both sides, §8.3). */
    discarded: z.number().int().nonnegative().default(0),
  })
  .strip();
export type PushResult = z.infer<typeof pushResultSchema>;

/* ------------------------------------- the operator side of the atelier (E) */

/**
 * What one synchronisation received and sent (§8.1): the counts the « Synchroniser »
 * button of the banner says (« 3 sessions, 1 relecture, 1 salle reçues »).
 * `done` is false when the run stopped on its time budget with pages left (H26: the
 * first one carries the whole history): the next run, which the atelier schedules
 * at once, goes on from the cursor.
 */
export const syncReportSchema = z
  .object({
    sessions: z.number().int().nonnegative(),
    reviews: z.number().int().nonnegative(),
    rooms: z.number().int().nonnegative(),
    trash: z.number().int().nonnegative(),
    tombstones: z.number().int().nonnegative(),
    pointers: z.number().int().nonnegative(),
    /** The review operations and the trash changes the atelier SENT before it pulled. */
    sentReviews: z.number().int().nonnegative(),
    sentTrash: z.number().int().nonnegative(),
    /** The versions of the same identifier and another content met on the way (§8.2). */
    anomalies: z.number().int().nonnegative(),
    /** The changes of a kind this code does not know, set aside; the cursor moved all the same. */
    skipped: z.number().int().nonnegative().default(0),
    pages: z.number().int().nonnegative(),
    done: z.boolean(),
    /** The sentence of the button, in French. */
    summary: z.string().min(1),
  })
  .strict();
export type SyncReport = z.infer<typeof syncReportSchema>;

/** One anomaly of the synchronisation, as « État de la prod » lists it (§8.2, §9.3). */
export const syncAnomalySchema = z
  .object({
    id: z.string().min(1),
    kind: syncFrozenKindSchema,
    entityId: z.string().min(1),
    versionId: z.string().min(1),
    versionNumber: z.number().int().positive(),
    /** `content`: the same identifier and another content; `number`: the number is taken by another version. */
    reason: z.enum(['content', 'number']),
    sessionId: z.string().min(1),
    detectedAt: z.string().min(1),
    /** The content the atelier knows and the one the vitrine sent, kept for the screen to show both. */
    localContent: syncJsonObjectSchema.nullable(),
    prodContent: syncJsonObjectSchema,
  })
  .strict();
export type SyncAnomaly = z.infer<typeof syncAnomalySchema>;

/**
 * The state of the synchronisation on the atelier (§9.3), `GET /api/atelier/sync`:
 * what the banner and « État de la prod » read. `configured` is false where
 * `PROD_ORIGIN` is not posed (a workstation, the vitrine), and then nothing else
 * means anything.
 */
export const syncStatusSchema = z
  .object({
    configured: z.boolean(),
    running: z.boolean(),
    runningSince: z.string().nullable(),
    cursor: z.number().int().nonnegative(),
    lastSuccessAt: z.string().nullable(),
    lastError: z.string().nullable(),
    lastErrorAt: z.string().nullable(),
    /** The report of the last run of this process, null before one. */
    lastReport: syncReportSchema.nullable(),
    anomalies: z.array(syncAnomalySchema),
    /**
     * The code the prod runs, as `GET /api/instance` of the prod last said it, and when this server
     * read it (§9.3, lot F). Null while nothing was read: a prod that predates `/api/instance`, one
     * that answers off the contract, or a process that has not asked yet. A prod that stops answering
     * leaves the last value and its instant, so the banner can say « lu à 13:00 ». Defaults to null
     * for a reader of a server that predates it.
     */
    prod: z
      .object({ code: z.string().min(1), readAt: z.string().min(1) })
      .strict()
      .nullable()
      .default(null),
  })
  .strict();
export type SyncStatus = z.infer<typeof syncStatusSchema>;

/* ------------------------------------------------- « État de la prod » (lot F) */

/**
 * The kinds « État de la prod » lists, in the order of the maquette (§9.3): the four libraries, then
 * the four singletons - the texts of the noyaux, the models, the rules, the method.
 */
export const PROD_STATE_KINDS = [
  'criterion',
  'persona',
  'protocol',
  'experience',
  'noyau-texts',
  'models',
  'rules',
  'method',
] as const satisfies readonly z.infer<typeof versionedKindSchema>[];

/**
 * An entity whose « latest dev » is ahead of « latest prod » (§9.3): a candidate for the passage. The
 * atelier lists it from its own base and never asks the prod: the SCREEN has the prod judge each line,
 * by `POST /api/atelier/promotion/check`, when it opens.
 */
export const prodStateCandidateSchema = z
  .object({
    kind: versionedKindSchema,
    entityId: z.string().min(1),
    name: z.string().min(1),
    /** The version « latest dev » sits on. */
    development: versionSummarySchema,
    /** The version « latest prod » sits on, or null when nothing was ever passed. */
    production: versionSummarySchema.nullable(),
  })
  .strict();
export type ProdStateCandidate = z.infer<typeof prodStateCandidateSchema>;

/** An anomaly of the synchronisation, with the name of the entity it is about (null once it is gone). */
export const prodStateAnomalySchema = syncAnomalySchema.extend({
  entityName: z.string().nullable(),
});
export type ProdStateAnomaly = z.infer<typeof prodStateAnomalySchema>;

/**
 * `GET /api/atelier/prod-state`: what the screen « État de la prod » reads first (§9.3). Local data
 * only, so it answers without a `PROD_ORIGIN` too.
 */
export const prodStateSchema = z
  .object({
    candidates: z.array(prodStateCandidateSchema),
    anomalies: z.array(prodStateAnomalySchema),
  })
  .strict();
export type ProdState = z.infer<typeof prodStateSchema>;

/** `POST /api/atelier/sync`: what the run received, and the state after it. */
export const syncRunResultSchema = z
  .object({ report: syncReportSchema, status: syncStatusSchema })
  .strict();
export type SyncRunResult = z.infer<typeof syncRunResultSchema>;
